// src/utils/useAssetPreview.js
// -----------------------------------------------------------------------------
// Lightweight React hook for asset preview prefetching + cache access.
// - Does nothing unless you call primePreviews([...ids], getToken)
// - Dynamic imports your fetcher only when needed
// -----------------------------------------------------------------------------

import * as React from "react";
import { previewCache } from "./assetCellHelpers";

// ────────────────────────────────────────────────────────────────
// ENTERPRISE: submit freeze gate + cooldown
// - Prevents preview prefetch from stealing time during submit AND
//   immediately after submit (when UI is repainting).
// - Single-source-of-truth lives on window so every component shares it.
//   • window.__ASSET_PREVIEW_FROZEN__   : boolean
//   • window.__ASSET_PREVIEW_UNTIL__    : epoch ms (cooldown end)
// ────────────────────────────────────────────────────────────────
if (typeof window !== "undefined") {
  if (window.__ASSET_PREVIEW_FROZEN__ == null) window.__ASSET_PREVIEW_FROZEN__ = false;
  if (window.__ASSET_PREVIEW_UNTIL__ == null) window.__ASSET_PREVIEW_UNTIL__ = 0;

  if (!window.__assetPreviewFreezeInstalled) {
    window.__assetPreviewFreezeInstalled = true;

    window.addEventListener("assetdetails:request-submit", () => {
      window.__ASSET_PREVIEW_FROZEN__ = true;
      // optional: extend cooldown while actively submitting
      window.__ASSET_PREVIEW_UNTIL__ = Math.max(window.__ASSET_PREVIEW_UNTIL__ || 0, Date.now() + 2000);
    });

    window.addEventListener("assetdetails:submit-status", () => {
      // Do NOT immediately unfreeze; set a short cooldown window.
      // This prevents primePreviews firing right after {assetId,...} swap.
      window.__ASSET_PREVIEW_FROZEN__ = false;
      window.__ASSET_PREVIEW_UNTIL__ = Date.now() + 2000; // 2s cooldown; tune 1200–3000ms
    });
  }
}

// getToken: optional async () => string|null
export function useAssetPreview() {
  const [, setTick] = React.useState(0);

  const getPreview = React.useCallback((id) => {
    if (!id) return { previewUrl: null, meta: null };
    return previewCache.get(id) || { previewUrl: null, meta: null };
  }, []);

  const primePreviews = React.useCallback(async (assetIds, getToken) => {
    // ✅ HARD GATE: frozen OR within cooldown window
    if (typeof window !== "undefined") {
      const frozen = window.__ASSET_PREVIEW_FROZEN__ === true;
      const until  = Number(window.__ASSET_PREVIEW_UNTIL__ || 0);
      if (frozen || Date.now() < until) return;
    }

    const missing = (assetIds || []).filter((id) => id && !previewCache.has(id));
    if (!missing.length) return;

    const token = getToken ? await getToken() : null;
    if (!token) return;

    try {
      const { fetchAssetById } = await import("./frontend_assetManager");

      for (const id of missing) {
        try {
          const data = await fetchAssetById(id, token, {
            includeUrls: true,
            dpr: (typeof window !== "undefined" ? window.devicePixelRatio : 1) || 1,
            includeMeta: true,
            prefer: "micro",
          });
          previewCache.set(id, { previewUrl: data?.previewUrl || null, meta: data?.meta || null });
        } catch {
          previewCache.set(id, { previewUrl: null, meta: null });
        }
      }

      setTick((n) => n + 1);
    } catch {
      // swallow dynamic import failures
    }
  }, []);

  return { primePreviews, getPreview };
}